export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export const DEFAULT_AUTH_TOKEN_STORAGE_KEY = 'crv-auth-tokens';
const memoryTokensByKey = new Map<string, AuthTokens | null>();

export function setTokens(tokens: AuthTokens, storageKey: string = DEFAULT_AUTH_TOKEN_STORAGE_KEY): void {
  memoryTokensByKey.set(storageKey, tokens);

  const storage = resolveStorage();
  if (storage) {
    storage.setItem(storageKey, JSON.stringify(tokens));
  }
}

export function getTokens(storageKey: string = DEFAULT_AUTH_TOKEN_STORAGE_KEY): AuthTokens | null {
  const storage = resolveStorage();
  if (storage) {
    const raw = storage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as AuthTokens;
        if (isAuthTokens(parsed)) {
          memoryTokensByKey.set(storageKey, parsed);
          return parsed;
        }
      } catch {
        storage.removeItem(storageKey);
      }
    }
  }

  return memoryTokensByKey.get(storageKey) ?? null;
}

export function clearTokens(storageKey: string = DEFAULT_AUTH_TOKEN_STORAGE_KEY): void {
  memoryTokensByKey.delete(storageKey);
  const storage = resolveStorage();
  storage?.removeItem(storageKey);
}

function resolveStorage(): StorageLike | null {
  const globalObj = globalThis as { localStorage?: StorageLike };
  if (!globalObj.localStorage) {
    return null;
  }

  try {
    return globalObj.localStorage;
  } catch {
    return null;
  }
}

function isAuthTokens(value: unknown): value is AuthTokens {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).accessToken === 'string' &&
    typeof (value as Record<string, unknown>).refreshToken === 'string'
  );
}
